import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import Caption from 'components/Luxkit/Typography/Caption'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppSelector } from 'hooks/reduxHooks'

const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(14)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const ProgressFill = styled.div`
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.round};

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalBackground};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalBackground};
  }

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
  }
`

const EndCap = styled.div`
  position: absolute;
  top: ${rem(2)};
  right: ${rem(2)};
  width: ${rem(10)};
  height: ${rem(10)};
  border-radius: ${props => props.theme.borderRadius.round};
  z-index: 1;

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalBackground};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalBackground};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
  }
`

function LoyaltyAccountNextTierProgressBar() {
  const {
    totalCreditsForTierRetention = 0,
    totalCreditsForTierUpgrade = 0,
    statusCredits = 0,
  } = useAppSelector(state => state.loyalty.account.data) ?? {}
  const [tier, tierName, nextTier, nextTierName] = useCurrentLoyaltyTier()

  const isRetentionOnly = !nextTier
  const progressPercent = useMemo(() => {
    return Math.min((statusCredits / totalCreditsForTierUpgrade) * 100, 100)
  }, [statusCredits, totalCreditsForTierUpgrade])

  const retentionPercent = useMemo(() => {
    return Math.min((totalCreditsForTierRetention / totalCreditsForTierUpgrade) * 100, 100)
  }, [totalCreditsForTierRetention, totalCreditsForTierUpgrade])

  return <VerticalSpacer gap={8}>
    <ProgressBarContainer>
      <ProgressFill
        style={{ width: `${isRetentionOnly ? retentionPercent : progressPercent}%` }}
        className={cn(`tier-${tier}`)}
      />
      <EndCap className={cn(`tier-${nextTier ?? tier}`)} />
    </ProgressBarContainer>
    <Caption variant="small" format="uppercase" weight="bold" align="end">
      {isRetentionOnly ? `retain ${tierName}` : `reach ${nextTierName}`}<br />
      <FormatDecimal value={isRetentionOnly ? totalCreditsForTierRetention : totalCreditsForTierUpgrade} />
    </Caption>
  </VerticalSpacer>
}
export default LoyaltyAccountNextTierProgressBar
